import React from "react";
import { motion } from "framer-motion";
import { Crown, Flag, Smile } from "lucide-react";
import { REACTIONS, timeStr, colorFor, initialOf } from "./chatUtils";

const MSG_INITIAL = { opacity: 0, y: 12 };
const MSG_ANIMATE = { opacity: 1, y: 0 };

export const ChatMessageItem = ({ m, myReactions, onReact, onReport }) => {
  const reactions = m.reactions || {};
  const pseudoColor = m.is_artist ? "#D4AF37" : colorFor(m.username);

  return (
    <motion.div
      initial={MSG_INITIAL}
      animate={MSG_ANIMATE}
      data-testid={`chat-msg-${m.id}`}
      className="flex gap-3 group"
    >
      {/* Avatar */}
      <div
        data-testid={`chat-avatar-${m.id}`}
        className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold select-none mt-0.5"
        style={
          m.is_artist
            ? { background: "linear-gradient(135deg,#D4AF37,#B8860B)", color: "#000" }
            : { background: `${pseudoColor}2E`, color: pseudoColor, border: `1.5px solid ${pseudoColor}` }
        }
      >
        {m.is_artist ? <Crown className="w-4 h-4" fill="currentColor" /> : initialOf(m.username)}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          {m.is_artist ? (
            <span className="flex items-center gap-1.5 text-sm font-medium" style={{ color: pseudoColor }}>
              {m.username}
              <span className="text-[9px] uppercase tracking-widest bg-gold text-black px-1.5 py-0.5">Artiste</span>
            </span>
          ) : (
            <span className="text-sm font-medium" style={{ color: pseudoColor }}>{m.username}</span>
          )}
          <span className="text-[10px] text-[#8A8A8A]">{timeStr(m.created_at)}</span>
          {!m.is_artist && (
            <button
              data-testid={`chat-report-${m.id}`}
              onClick={() => onReport(m.id)}
              title="Signaler ce message"
              className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity text-[#6A6A6A] hover:text-red-400 ml-auto"
            >
              <Flag className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        {m.reply_to && (
          <div data-testid={`chat-reply-${m.id}`} className="border-l-2 border-gold/40 pl-2 mb-1.5 max-w-full">
            <span className="text-[11px] text-gold/80">↳ {m.reply_to.username}</span>
            <p className="text-xs text-[#9A9A9A] italic break-words line-clamp-2">{m.reply_to.message}</p>
          </div>
        )}
        <div
          className={`inline-block px-4 py-2.5 max-w-full break-words text-sm leading-relaxed ${
            m.is_artist
              ? "border border-gold/40 bg-gold/[0.06] text-gold-light"
              : "bg-white/[0.04] border border-white/[0.06] text-white/90"
          }`}
        >
          {m.message}
        </div>

        {/* Reactions row */}
        <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
          {REACTIONS.map((emoji) => {
            const count = reactions[emoji] || 0;
            const mine = myReactions[`${m.id}:${emoji}`];
            if (count === 0) return null;
            return (
              <button
                key={emoji}
                data-testid={`chat-reaction-${m.id}-${emoji}`}
                onClick={() => onReact(m.id, emoji)}
                className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-xs transition-colors ${
                  mine
                    ? "bg-gold/20 border border-gold/50 text-gold-light"
                    : "bg-white/[0.05] border border-white/[0.08] text-white/70 hover:bg-white/[0.1]"
                }`}
              >
                <span>{emoji}</span>
                <span>{count}</span>
              </button>
            );
          })}
          {/* Add reaction menu */}
          <div className="relative opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
            <details className="reaction-menu">
              <summary
                data-testid={`chat-react-toggle-${m.id}`}
                className="list-none cursor-pointer rounded-full w-6 h-6 flex items-center justify-center bg-white/[0.05] border border-white/[0.08] text-white/50 hover:text-gold"
              >
                <Smile className="w-3.5 h-3.5" />
              </summary>
              <div className="absolute z-20 bottom-8 left-0 flex gap-1 bg-[#141414] border border-white/[0.12] rounded-full px-2 py-1 shadow-xl">
                {REACTIONS.map((emoji) => (
                  <button
                    key={emoji}
                    data-testid={`chat-react-add-${m.id}-${emoji}`}
                    onClick={(e) => {
                      onReact(m.id, emoji);
                      e.currentTarget.closest("details").removeAttribute("open");
                    }}
                    className="text-base hover:scale-125 transition-transform"
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </details>
          </div>
        </div>
      </div>
    </motion.div>
  );
};
